import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon/Icon';
import styles from './Modal.module.css';

// Reusable modal. Closes on overlay click and Escape. Renders via portal.
// `onBack` is optional: when provided, a back arrow appears in the header so
// the caller can drive a multi-step flow (wizard) without owning extra
// layout. The back affordance is independent from `onClose`, which always
// dismisses the dialog entirely.
export default function Modal({
  isOpen,
  onClose,
  onBack,
  title,
  children,
  footer,
  size = 'default',
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.dialog} ${size === 'large' ? styles.dialogLarge : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            {onBack && (
              <button
                type="button"
                className={styles.back}
                onClick={onBack}
                aria-label="Back"
                title="Back"
              >
                <Icon name="ArrowLeft" size={14} />
              </button>
            )}
            <h2 className={styles.title}>{title}</h2>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="X" size={16} />
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}
